import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  addChangelogRecipient,
  approveChangelogEntry,
  removeChangelogRecipient,
  revokeChangelogApproval,
  saveChangelogEntry,
  sendChangelogEntry,
  sendAlertsSlackTest,
  sendChangelogSlackTest,
  setChangelogRecipientActive,
  syncChangelog,
  regenerateChangelogNotes,
  updateChangelogSettings,
  type ChangelogChannel,
  type ChangelogEditInput,
  type ChangelogEntry,
  type ChangelogPage,
  type ChangelogRecipientsPage,
  type ChangelogSettingsInput,
} from "@/features/changelog/api/changelog.api";
import { changelogKeys } from "@/lib/queries";

/**
 * Every write in this tab reports its own failure next to the control that
 * caused it, so the global error toast would be a second, less useful copy.
 */
const LOCAL_ERRORS = { meta: { skipGlobalError: true } } as const;

/**
 * A write returns the entry it changed, so the list can be patched in place
 * rather than refetched — the card keeps its scroll position and the approve
 * button flips the moment the server agrees.
 */
function useEntryWriter() {
  const queryClient = useQueryClient();

  return (entry: ChangelogEntry) => {
    queryClient.setQueryData<ChangelogPage>(changelogKeys.list, (page) =>
      page
        ? {
            ...page,
            entries: page.entries.map((e) => (e.versionTag === entry.versionTag ? entry : e)),
          }
        : page,
    );
  };
}

/**
 * Saves the changelog settings and seeds the cache with the server's copy, so
 * the deploys tab picks up a new sprint length without a refetch.
 *
 * The list is invalidated too: a sprint anchor or cadence change moves which
 * window the filters describe, and leaving a stale one on screen would have the
 * page disagreeing with the setting somebody just saved.
 */
export function useSaveChangelogSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Partial<ChangelogSettingsInput>) => updateChangelogSettings(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(changelogKeys.settings, settings);
      void queryClient.invalidateQueries({ queryKey: changelogKeys.list });
    },
    ...LOCAL_ERRORS,
  });
}

/**
 * Fires one throwaway message at the configured webhook. No cache to touch —
 * the point is the ping in the Slack channel, and the reply saying it landed.
 */
export function useSendChangelogSlackTest() {
  return useMutation({
    mutationFn: sendChangelogSlackTest,
    ...LOCAL_ERRORS,
  });
}

/** The same ping at the admin-alerts webhook. */
export function useSendAlertsSlackTest() {
  return useMutation({
    mutationFn: sendAlertsSlackTest,
    ...LOCAL_ERRORS,
  });
}

export function useSyncChangelog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncChangelog,
    onSuccess: (page) => queryClient.setQueryData(changelogKeys.list, page),
    ...LOCAL_ERRORS,
  });
}

export function useRegenerateChangelog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: regenerateChangelogNotes,
    // Regeneration answers before the worker starts, so there is no fresh entry
    // to patch in — refetching is what picks up the GENERATING status that
    // starts the poll.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: changelogKeys.list }),
    ...LOCAL_ERRORS,
  });
}

export function useSaveChangelogEntry() {
  const writeEntry = useEntryWriter();

  return useMutation({
    mutationFn: (input: ChangelogEditInput) => saveChangelogEntry(input),
    onSuccess: writeEntry,
    ...LOCAL_ERRORS,
  });
}

export function useApproveChangelogEntry() {
  const writeEntry = useEntryWriter();

  return useMutation({
    mutationFn: ({
      versionTag,
      expectedUpdatedAt,
    }: {
      versionTag: string;
      expectedUpdatedAt: string;
    }) => approveChangelogEntry(versionTag, expectedUpdatedAt),
    onSuccess: writeEntry,
    ...LOCAL_ERRORS,
  });
}

export function useRevokeChangelogApproval() {
  const writeEntry = useEntryWriter();

  return useMutation({
    mutationFn: (versionTag: string) => revokeChangelogApproval(versionTag),
    onSuccess: writeEntry,
    ...LOCAL_ERRORS,
  });
}

export function useSendChangelogEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionTag,
      channels,
      force,
    }: {
      versionTag: string;
      channels: ChangelogChannel[];
      force?: boolean;
    }) => sendChangelogEntry(versionTag, channels, force),
    // Delivery finishes in a worker, so the reply's poll hint has to reach the
    // page level: patching the entry alone would leave the tab showing "sending"
    // with nothing scheduled to find out that it finished.
    onSuccess: ({ pollAfterMs, ...entry }) => {
      queryClient.setQueryData<ChangelogPage>(changelogKeys.list, (page) =>
        page
          ? {
              ...page,
              pollAfterMs: pollAfterMs ?? page.pollAfterMs,
              entries: page.entries.map((e) => (e.versionTag === entry.versionTag ? entry : e)),
            }
          : page,
      );
    },
    ...LOCAL_ERRORS,
  });
}

/** Recipient writes all return the whole list, so the cache takes it verbatim. */
function useRecipientsWriter() {
  const queryClient = useQueryClient();

  return (page: ChangelogRecipientsPage) =>
    queryClient.setQueryData(changelogKeys.recipients, page);
}

export function useAddChangelogRecipient() {
  const writeRecipients = useRecipientsWriter();

  return useMutation({
    mutationFn: addChangelogRecipient,
    onSuccess: writeRecipients,
    ...LOCAL_ERRORS,
  });
}

export function useSetChangelogRecipientActive() {
  const writeRecipients = useRecipientsWriter();

  return useMutation({
    mutationFn: ({ email, isActive }: { email: string; isActive: boolean }) =>
      setChangelogRecipientActive(email, isActive),
    onSuccess: writeRecipients,
    ...LOCAL_ERRORS,
  });
}

export function useRemoveChangelogRecipient() {
  const writeRecipients = useRecipientsWriter();

  return useMutation({
    mutationFn: (recipientId: string) => removeChangelogRecipient(recipientId),
    onSuccess: writeRecipients,
    ...LOCAL_ERRORS,
  });
}
